import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  AttachMoney as MoneyIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

const StyledCard = styled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: "blur(10px)",
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[10],
}));

const PaystubsTab: React.FC = () => {
  const theme = useTheme();

  const paystubs = [
    {
      id: 1,
      period: "December 2024",
      payDate: "2024-12-31",
      grossPay: "$5,000.00",
      netPay: "$3,847.50",
      status: "Available",
    },
    {
      id: 2,
      period: "November 2024",
      payDate: "2024-11-30",
      grossPay: "$5,000.00",
      netPay: "$3,847.50",
      status: "Available",
    },
    {
      id: 3,
      period: "October 2024",
      payDate: "2024-10-31",
      grossPay: "$5,000.00",
      netPay: "$3,847.50",
      status: "Available",
    },
  ];

  const yearToDateSummary = {
    totalGross: "$60,000.00",
    totalNet: "$46,170.00",
    totalTaxes: "$13,830.00",
    benefits: "$2,400.00",
  };

  const handleDownload = (paystubId: number) => {
    console.log(`Downloading paystub ${paystubId}`);
  };

  return (
    <Grid container spacing={3}>
      {/* YTD Summary Cards */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
          <MoneyIcon color="primary" sx={{ mr: 1 }} />
          Year-to-Date Summary
        </Typography>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="primary" gutterBottom>
              {yearToDateSummary.totalGross}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Gross Pay
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="success.main" gutterBottom>
              {yearToDateSummary.totalNet}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Net Pay
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="warning.main" gutterBottom>
              {yearToDateSummary.totalTaxes}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Taxes
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      <Grid item xs={12} sm={6} md={3}>
        <StyledCard>
          <CardContent sx={{ textAlign: "center" }}>
            <Typography variant="h4" color="info.main" gutterBottom>
              {yearToDateSummary.benefits}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Benefits Deducted
            </Typography>
          </CardContent>
        </StyledCard>
      </Grid>

      {/* Paystubs Table */}
      <Grid item xs={12}>
        <StyledCard>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
              <ReceiptIcon color="primary" sx={{ mr: 1 }} />
              Recent Paystubs
            </Typography>
            <TableContainer component={Paper} sx={{ mt: 2, borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                    <TableCell><strong>Pay Period</strong></TableCell>
                    <TableCell><strong>Pay Date</strong></TableCell>
                    <TableCell><strong>Gross Pay</strong></TableCell>
                    <TableCell><strong>Net Pay</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paystubs.map((paystub) => (
                    <TableRow
                      key={paystub.id}
                      hover
                      sx={{ "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.05) } }}
                    >
                      <TableCell>{paystub.period}</TableCell>
                      <TableCell>{paystub.payDate}</TableCell>
                      <TableCell>{paystub.grossPay}</TableCell>
                      <TableCell>{paystub.netPay}</TableCell>
                      <TableCell>
                        <Chip
                          label={paystub.status}
                          color="success"
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownload(paystub.id)}
                        >
                          Download
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </StyledCard>
      </Grid>
    </Grid>
  );
};

export default PaystubsTab;